import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, MessageCircle } from "lucide-react";
import { NAV_LINKS, ASSETS, COMPANY, waLink, WA_MESSAGES } from "@/lib/site";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-border shadow-soft"
          : "bg-white/60 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-28 items-center justify-between gap-4">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-3 shrink-0">
            <img src={ASSETS.logo} alt="Miríade Turismo" className="h-20 w-20 sm:h-24 sm:w-24 rounded-full object-cover ring-2 ring-brand-light" />
          </Link>

          <nav className="hidden items-center gap-1 lg:flex">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.to === "/" ? "home" : l.to.replace("/", "")}`}
                className={({ isActive }) =>
                  `rounded-full px-3.5 py-2 text-sm font-medium transition-colors ${
                    isActive ? "text-brand bg-brand-light" : "text-ink-soft hover:text-brand hover:bg-brand-light/60"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <a
              href={waLink(WA_MESSAGES.default)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-whatsapp-btn"
              className="hidden items-center gap-2 rounded-full bg-[#25D366] px-5 py-2.5 text-sm font-semibold text-white shadow-soft transition-all hover:scale-[1.03] hover:bg-[#1ebe5d] sm:flex"
            >
              <MessageCircle size={18} /> WhatsApp
            </a>
            <button
              onClick={() => setOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              aria-label="Abrir menu"
              className="rounded-full p-2.5 text-brand transition-colors hover:bg-brand-light lg:hidden"
            >
              {open ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div data-testid="mobile-menu" className="border-t border-border bg-white lg:hidden">
          <nav className="mx-auto flex max-w-7xl flex-col gap-1 px-4 py-4">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-${l.to === "/" ? "home" : l.to.replace("/", "")}`}
                className={({ isActive }) =>
                  `rounded-2xl px-4 py-3 text-base font-medium transition-colors ${
                    isActive ? "bg-brand-light text-brand" : "text-ink hover:bg-brand-light/60"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <a
              href={waLink(WA_MESSAGES.default)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-whatsapp-btn"
              className="mt-2 flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-5 py-3.5 text-base font-semibold text-white"
            >
              <MessageCircle size={20} /> Falar no WhatsApp
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};
